import { useParams, Link } from 'react-router-dom';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SEOMeta from '@/components/feature/SEOMeta';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;
  const articles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900">分类未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 不良研究所`,
    description: category.description,
    url: `https://buliangyanjiusuo.com/category/${slug}`,
    mainEntity: articles.map((article) => ({
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      url: `https://buliangyanjiusuo.com/article/${article.slug}`,
      image: article.imageUrl,
      author: { '@type': 'Person', name: article.author },
      datePublished: article.publishDate,
    })),
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${category.name} - 不良研究所`}
        description={category.description}
        keywords={`不良研究所,${category.name},深度研究内容,独立创意研究,非主流内容探索`}
        canonicalUrl={`https://buliangyanjiusuo.com/category/${slug}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />
        </div>

        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-8">
          <div className="bg-background-100 rounded-lg p-8 md:p-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-600">
                <i className={`${category.icon} text-lg`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                {category.name}
              </h1>
            </div>
            <p className="text-sm text-foreground-600 max-w-2xl leading-relaxed">
              {category.description}
            </p>
            <p className="text-xs text-foreground-500 mt-2">
              共 {articles.length} 篇文章
            </p>
          </div>
        </section>

        {articles.length > 0 ? (
          <section className="max-w-7xl mx-auto px-4 md:px-6 pb-20">
            <div className="grid grid-cols-1 gap-4">
              {articles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </section>
        ) : (
          <section className="max-w-7xl mx-auto px-4 md:px-6 pb-20 text-center py-16">
            <p className="text-foreground-500">该分类暂无文章</p>
          </section>
        )}

        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-14">
          <h3 className="text-sm font-semibold text-foreground-900 mb-4">浏览其他分类</h3>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white font-medium'
                    : 'bg-background-50 text-foreground-600 hover:bg-background-100 hover:text-primary-600'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}